import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Icon } from '@iconify/react';

const creativeTools = [
  {
    name: 'Premiere Pro',
    icon: 'logos:adobe-premiere',
    position: 'top-[8%] left-[6%] md:left-[10%]',
    delay: '0.0s'
  },
  {
    name: 'DaVinci Resolve',
    icon: 'simple-icons:davinciresolve',
    iconColor: '#233A51',
    position: 'top-[6%] left-1/2 -translate-x-1/2',
    delay: '0.6s'
  },
  {
    name: 'Gemini',
    icon: 'logos:google-gemini',
    position: 'top-[10%] right-[7%] md:right-[12%]',
    delay: '1.1s'
  },
  {
    name: 'Google',
    icon: 'logos:google-icon',
    position: 'top-[42%] left-[4%] md:left-[8%]',
    delay: '0.4s'
  },
  {
    name: 'Apple',
    icon: 'logos:apple',
    position: 'top-[44%] right-[7%] md:right-[12%]',
    delay: '0.9s'
  },
  {
    name: 'CapCut Pro',
    icon: 'hugeicons:capcut',
    iconColor: '#101113',
    position: 'bottom-[18%] left-[9%] md:left-[13%]',
    delay: '1.5s'
  },
  {
    name: 'Canva',
    icon: 'devicon:canva',
    position: 'bottom-[15%] right-[7%] md:right-[11%]',
    delay: '0.2s'
  },
  {
    name: 'Claude',
    icon: 'logos:claude-icon',
    position: 'bottom-[8%] left-1/2 -translate-x-1/2 hidden sm:flex',
    delay: '1.8s'
  },
  {
    name: 'Higgsfield',
    image: 'https://higgsfield.ai/favicon.ico',
    position: 'top-[24%] left-[24%] hidden md:flex',
    delay: '1.2s'
  }
];

const CreativeSkillsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="habilidades" className="relative overflow-hidden bg-white py-24 md:py-32">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(0,0,0,0.05),transparent_34%),radial-gradient(circle_at_82%_30%,rgba(0,0,0,0.045),transparent_38%),radial-gradient(circle_at_52%_84%,rgba(0,0,0,0.035),transparent_36%)]" />

        <svg className="absolute left-[20%] top-[24%] hidden h-16 w-56 text-black/10 md:block" viewBox="0 0 220 60" fill="none" aria-hidden="true">
          <path d="M2 42C60 8 130 8 218 42" stroke="currentColor" strokeWidth="1.25" />
        </svg>
        <svg className="absolute right-[22%] top-[24%] hidden h-16 w-56 text-black/10 md:block" viewBox="0 0 220 60" fill="none" aria-hidden="true">
          <path d="M2 42C60 8 130 8 218 42" stroke="currentColor" strokeWidth="1.25" />
        </svg>
        <svg className="absolute left-[22%] bottom-[26%] hidden h-16 w-56 text-black/10 md:block" viewBox="0 0 220 60" fill="none" aria-hidden="true">
          <path d="M2 42C60 8 130 8 218 42" stroke="currentColor" strokeWidth="1.25" />
        </svg>
        <svg className="absolute right-[22%] bottom-[26%] hidden h-16 w-56 text-black/10 md:block" viewBox="0 0 220 60" fill="none" aria-hidden="true">
          <path d="M2 42C60 8 130 8 218 42" stroke="currentColor" strokeWidth="1.25" />
        </svg>
      </div>

      {creativeTools.map((tool) => (
        <div
          key={tool.name}
          className={`absolute z-10 ${tool.position}`}
        >
          <div className="group relative flex items-center justify-center">
            <div
              className="animate-soft-float flex h-12 w-12 items-center justify-center rounded-2xl border border-black/10 bg-white/95 text-sm shadow-[0_12px_24px_-12px_rgba(0,0,0,0.45)] backdrop-blur-sm transition-shadow duration-200 group-hover:shadow-[0_18px_32px_-14px_rgba(0,0,0,0.5)] md:h-14 md:w-14"
              style={{ animationDelay: tool.delay || '0s' }}
              title={tool.name}
            >
              {tool.icon && (
                <Icon
                  icon={tool.icon}
                  color={tool.iconColor}
                  className="h-6 w-6 md:h-7 md:w-7"
                  aria-hidden="true"
                />
              )}
              {tool.image && (
                <img
                  src={tool.image}
                  alt={tool.name}
                  loading="lazy"
                  className="h-6 w-6 rounded-md md:h-7 md:w-7"
                />
              )}
            </div>

            <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black px-2.5 py-1 text-[10px] font-semibold tracking-[0.08em] text-white opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
              {tool.name}
            </span>
          </div>
        </div>
      ))}

      <div className="relative z-20 mx-auto max-w-3xl px-6 text-center">
        <p className="mx-auto mb-6 inline-block rounded-full bg-black/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-black/50">
          {t('skills.badge')}
        </p>
        <h2 className="text-4xl font-semibold tracking-tight leading-[1.05] text-black sm:text-5xl md:text-6xl">
          {t('skills.title')}
        </h2>
      </div>
    </section>
  );
};

export default CreativeSkillsSection;